/**
 * Resolve a Sage salesperson code (e.g. "ER") to a human-readable name.
 *
 * Orders carry a denormalized `salesperson_name`, but several creation paths
 * leave it blank — `services/orders/salesperson_derivation.py` returns an
 * empty name when the Sage `salespersons` mirror has no row (or a null name),
 * and manual order create defaults it to "". When that happened the order view
 * fell back to the bare Sage code, so CSRs saw cryptic values like "ER" with
 * no way to tell who that is (ER is the order-desk code for Emily).
 *
 * Resolution order: stored name -> salespersons reference list -> raw code.
 * The raw code is still preferred over showing nothing, so a code we cannot
 * resolve degrades to today's behaviour rather than hiding the value.
 */

/** Minimal shape needed to resolve a code; structurally matches SalespersonOption. */
export interface SalespersonNameSource {
  salesperson_no: string;
  name?: string | null;
  /** In-house regional responsible for the code, when the reference list carries it. */
  regional_responsible_name?: string | null;
}

/**
 * @param storedName  the order's denormalized `salesperson_name` (may be blank)
 * @param code        the order's `salesperson_no` Sage code (e.g. "ER", "MR")
 * @param options     salespersons reference list, from GET /portal/salespersons
 * @param fallback    rendered when there is neither a name nor a code
 */
export function resolveSalespersonDisplay(
  storedName: string | null | undefined,
  code: string | null | undefined,
  options: readonly SalespersonNameSource[] = [],
  fallback = '-',
): string {
  const name = storedName?.trim();
  if (name) return name;

  const sageCode = code?.trim();
  if (!sageCode) return fallback;

  const target = sageCode.toLowerCase();
  const match = options.find(
    (option) => option.salesperson_no?.trim().toLowerCase() === target,
  );
  const resolved = match?.name?.trim();

  return resolved || sageCode;
}

/**
 * Resolve the regional responsible (in-house core salesperson) for a Sage
 * salesperson code from the salespersons reference list.
 *
 * The code of record on an order or customer is often an outside rep agency
 * (e.g. "WA" Weemhoff - Ohio); the regional responsible is the ChemMasters
 * salesperson that agency reports to (Rich Tanski), as the customers list
 * shows in its "Regional Resp." column. Order-desk codes (ER / RB) and
 * codes missing from the list have no in-house rep and resolve to null so
 * the caller can render an explicit "none" instead of an empty string.
 */
export function resolveRegionalResponsible(
  code: string | null | undefined,
  options: readonly SalespersonNameSource[] = [],
): string | null {
  const sageCode = code?.trim();
  if (!sageCode) return null;
  const target = sageCode.toLowerCase();
  const match = options.find(
    (option) => option.salesperson_no?.trim().toLowerCase() === target,
  );
  const name = match?.regional_responsible_name?.trim();
  return name || null;
}
